/** @format */
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import type { SessionStorageConfig } from '@shared/data-access/interfaces';
import { Logger } from './logger.service';

export const SESSION_STORAGE_CONFIG = new InjectionToken<SessionStorageConfig>(
  'sessionStorageConfig',
);

export const DEFAULT_SESSION_STORAGE_CONFIG: SessionStorageConfig = {
  allowNull: true,
  prefix: 'DCSG_NGX_',
};

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private readonly config = inject(SESSION_STORAGE_CONFIG, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(Logger);

  private readonly _prefix = this.config?.prefix ?? DEFAULT_SESSION_STORAGE_CONFIG.prefix;
  private readonly _allowNull = this.config?.allowNull ?? DEFAULT_SESSION_STORAGE_CONFIG.allowNull;

  private logError(error: unknown): void {
    this.logger.error(`SESSION_STORAGE_ERROR: ${this.logger.getErrorMessage(error)}`, error);
  }

  /**
   * @description
   * Gets the entry specified by the given key or null
   *
   * @param key - Key identifying the wanted entry
   * @param prefix - Optional prefix to overwrite the configured one
   * @returns The value for a given key/prefix
   */
  get(key: string, prefix?: string): string | null | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    prefix ??= this._prefix;

    try {
      const returnValue = sessionStorage.getItem(`${prefix}${key}`);

      // Check for string "null"
      if (returnValue === 'null') {
        return null;
      }

      // Check for string "undefined"
      if (returnValue === 'undefined') {
        return undefined;
      }

      return returnValue;
    } catch (error) {
      this.logError(error);
    }
  }

  /**
   * @description
   * Removes the entry specified by the given key
   *
   * @param key - Key identifying the entry to remove
   * @param prefix - Optional prefix to overwrite the configured one
   */
  remove(key: string, prefix?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    prefix ??= this._prefix;

    try {
      sessionStorage.removeItem(`${prefix}${key}`);
    } catch (error) {
      this.logError(error);
    }
  }

  /**
   * @description
   * Clears all entries of the application's session storage
   */
  removeAll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.clear();
    } catch (error) {
      this.logError(error);
    }
  }

  /**
   * @description
   * Adds the value with the given key or updates an existing entry
   *
   * @param key - Key to store
   * @param value - Value to store
   * @param prefix - Optional prefix to overwrite the configured one
   */
  set(key: string, value: string, prefix?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    prefix ??= this._prefix;

    if (this._allowNull || (!this._allowNull && value !== 'null')) {
      try {
        sessionStorage.setItem(`${prefix}${key}`, value);
      } catch (error) {
        this.logError(error);
      }
    } else {
      this.remove(key, prefix);
    }
  }
}
